/**
 * THE icon pack — every AI-generated SVG icon lives here, grouped into `avatar/ reward/ concept/ spot/` and
 * addressed by a unique `"group/name"` (the group prefix keeps names unique across sets — e.g. `reward/gift`
 * ≠ `concept/gift`). Generated via svgmaker (flat, soft-rounded, dark-slate outline); per-group provenance in
 * `./<group>/_generated.json`. Design: docs/brand/icon-storage.md + icon-generation-playbook.md.
 *
 * ADD A NEW ICON → just drop `name.svg` into the right group dir. The eager glob picks it up automatically;
 * reference it by `group/name` via {@link iconUrl} / `<Icon>`, or via the domain accessor for a keyed set.
 * No import lines, no map edits.
 *
 * Vite content-hashes each file at build (cache-busted + precached offline by the PWA glob). The eager
 * `?url` glob only builds a small name→url string map — the SVGs themselves load on demand through `<img src>`.
 */
const modules = import.meta.glob('./*/*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/** Unified registry: `"group/name"` → hashed URL (`avatar/fox`, `reward/gift`, `concept/flame`, `spot/empty-inbox`). */
export const ICONS: Record<string, string> = Object.fromEntries(
  Object.entries(modules).map(([path, url]) => [path.replace(/^\.\/(.+)\.svg$/, '$1'), url]),
);

/** The hashed URL for a pack icon by its `"group/name"`, or `undefined` if there is no such icon. */
export function iconUrl(name: string): string | undefined {
  return ICONS[name];
}

/** Avatar art by member avatar key (the `AVATAR_GLYPHS` keys); `undefined` → caller shows the emoji. */
export function avatarArt(key?: string | null): string | undefined {
  return key ? ICONS[`avatar/${key}`] : undefined;
}

/** Reward art by reward `iconKey`; `undefined` → caller shows the emoji. */
export function rewardArt(key?: string | null): string | undefined {
  return key ? ICONS[`reward/${key}`] : undefined;
}

/** Concept art by concept name (HISTORY/METRIC/SOURCE/TIME registries); `""` if absent → `<ConceptIcon>` shows the emoji. */
export function conceptArt(name: string): string {
  return ICONS[`concept/${name}`] ?? '';
}

/** Spot-illustration semantic keys (e.g. `"empty.inbox"`) → pack names (`"spot/empty-inbox"`). */
const SPOT_ALIAS: Record<string, string> = {
  'empty.inbox': 'spot/empty-inbox',
  'empty.rewards': 'spot/empty-rewards',
  'empty.members': 'spot/empty-members',
  'empty.chores': 'spot/empty-chores',
  'error.offline': 'spot/error-offline',
  // Expansion (2026-06-24): reuse existing spot art + pack content icons, + 5 new spot files.
  'empty.children': 'spot/empty-members',
  'empty.calendar': 'spot/empty-chores',
  'empty.events': 'spot/empty-inbox',
  'empty.habits': 'concept/sprout',
  'empty.wallet': 'concept/money-bag',
  'empty.curriculum': 'reward/book',
  'empty.wishlist': 'reward/heart',
  'empty.roles': 'spot/empty-roles',
  'empty.categories': 'spot/empty-categories',
  'empty.logs': 'spot/empty-logs',
  'empty.search': 'spot/empty-search',
  'empty.quests': 'spot/empty-quests',
  // Straggler sweep (2026-06-24): 2 new spot files + reuse for feeds/templates.
  'empty.locked': 'spot/locked',
  'empty.households': 'spot/empty-households',
  'empty.activity': 'spot/empty-inbox',
  'empty.templates': 'reward/book',
};

/** Spot illustration by its semantic key (the {@link Illustration} component); `undefined` → emoji fallback. */
export function spotArt(name?: string | null): string | undefined {
  return name ? ICONS[SPOT_ALIAS[name] ?? name] : undefined;
}

/** Chore keys that REUSE an existing pack icon instead of a dedicated `chore/` file (DRY cross-group reuse). */
const CHORE_ALIAS: Record<string, string> = {
  room: 'concept/broom',
  table: 'reward/choose-dinner',
  plants: 'reward/garden',
  read: 'reward/book',
  pet: 'reward/pet',
  groceries: 'concept/shopping-bag',
};

/** Chore art by chore icon key — a shared icon where one fits, else `chore/<key>`; `undefined` → emoji. */
export function choreArt(key?: string | null): string | undefined {
  if (!key) return undefined;
  return ICONS[CHORE_ALIAS[key] ?? `chore/${key}`];
}

/** Habit keys that REUSE an existing pack icon instead of a dedicated `habit/` file (DRY cross-group reuse). */
const HABIT_ALIAS: Record<string, string> = {
  teeth: 'chore/teeth',
  water: 'chore/water',
  soap: 'chore/soap',
  shower: 'chore/shower',
  tidy: 'concept/broom',
  read: 'reward/book',
  music: 'reward/music',
  outside: 'reward/tree',
  star: 'reward/sticker',
  apple: 'reward/fruit',
  gratitude: 'reward/heart',
  heart: 'reward/heart',
  sleep: 'chore/bed',
  hands: 'chore/help',
  screen: 'reward/screen',
  // Expansion (2026-06-24) — reuse pack icons across groups.
  bed: 'chore/bed',
  dishes: 'chore/dishes',
  plants: 'reward/garden',
  'walk-dog': 'chore/walk-dog',
  recycle: 'chore/recycle',
  clothes: 'chore/clothes',
  backpack: 'chore/backpack',
  bike: 'reward/bike',
  sport: 'reward/soccer',
  breakfast: 'reward/pancakes',
  vitamins: 'chore/medicine',
  wake: 'concept/sunrise',
  hair: 'chore/hair',
  homework: 'chore/homework',
  instrument: 'chore/instrument',
  friends: 'reward/friends',
};

/** Habit art by habit icon key — a shared icon where one fits, else `habit/<key>`; `undefined` → emoji. */
export function habitArt(key?: string | null): string | undefined {
  if (!key) return undefined;
  return ICONS[HABIT_ALIAS[key] ?? `habit/${key}`];
}

/** Quest types that REUSE an existing pack icon (the others have a dedicated `quest/<type>` file). */
const QUEST_ALIAS: Record<string, string> = {
  daily: 'concept/sun',
  weekly: 'concept/calendar-check',
  story: 'reward/book',
};

/** Quest art by `questType` (ADR-0032 §7, presentational) — shared icon where one fits, else `quest/<type>`;
 *  `undefined` → caller shows the emoji (`questTypeGlyph`). */
export function questArt(type?: string | null): string | undefined {
  if (!type) return undefined;
  return ICONS[QUEST_ALIAS[type] ?? `quest/${type}`];
}

/** Learning SUBJECT root-card → pack icon; `undefined` → caller shows the emoji (`subjectGlyph`). */
const SUBJECT_ART: Record<string, string> = {
  math: 'topic/abacus',
  reading: 'reward/book',
  time: 'concept/clock',
  // ADR-0128/0129 subjects (added after the original pack mapping): the speech bubble for the foreign
  // language, the pencil for native literacy, the sprout for exploring the world.
  ukr: 'habit/pencil',
  eng: 'topic/phonics',
  svit: 'concept/sprout',
};

/** Subject art by subject key; `undefined` → emoji fallback (a guardian-authored pack subject has no art). */
export function subjectArt(subject?: string | null): string | undefined {
  return subject ? ICONS[SUBJECT_ART[subject] ?? ''] : undefined;
}

/**
 * Learning TOPIC id → pack icon (ADR-0034 — each topic's face on the path). Reuses a shared concept/reward/
 * habit icon where one fits, else a dedicated `topic/` file; repeated math concepts (the three times-tables,
 * both add-within lanes, the counting/place-value lanes) deliberately share one icon. `undefined` → caller
 * shows the emoji (`topicGlyph`, a book for `pack:` / unknown topics).
 */
const TOPIC_ART: Record<string, string> = {
  'math.spatial': 'topic/spatial',
  'math.shapes': 'topic/shapes',
  'math.counting': 'topic/ten-frame',
  'math.compare-10': 'concept/scale',
  'math.number-bonds': 'topic/number-bonds',
  'math.add-within-10': 'topic/addition',
  'math.subtraction': 'topic/subtraction',
  'math.skip-count': 'concept/hand',
  'math.numbers-to-20': 'topic/ten-frame',
  'math.numbers-to-100': 'topic/ten-frame',
  'math.measure': 'topic/measure',
  'math.money': 'concept/money-bag',
  'math.time-units': 'concept/calendar-check',
  'math.add-within-20': 'topic/addition',
  'math.add-sub-100': 'topic/abacus',
  'math.multiplication': 'topic/multiplication',
  'math.times-tables-easy': 'topic/multiplication',
  'math.times-tables-hard': 'topic/multiplication',
  'math.mul-compare': 'concept/chart-up',
  'math.expressions': 'concept/scale',
  'math.word-problems': 'habit/pencil',
  'math.word-problems-multi': 'reward/puzzle',
  'math.geometry': 'topic/geometry',
  'math.perimeter': 'topic/geometry',
  'math.data': 'topic/bars',
  // ukr / eng / svit + the g1-g2 volume-pass math topics (all REUSE — no new assets; repeated concepts
  // deliberately share one icon, the established pack norm):
  'math.attributes': 'reward/blocks',
  'math.lines': 'topic/geometry',
  'math.word-problems-10': 'habit/pencil',
  'math.patterns': 'reward/dice',
  'math.division-facts': 'topic/multiplication',
  'math.fractions': 'reward/pizza',
  'math.expr-intro': 'topic/addition',
  'math.length-units': 'topic/measure',
  'eng.alphabet': 'reward/blocks',
  'eng.greetings': 'reward/friends',
  'eng.numbers': 'topic/ten-frame',
  'eng.colours': 'reward/crayons',
  'eng.family': 'reward/picnic',
  'eng.animals': 'reward/pet',
  'eng.food': 'reward/fruit',
  'eng.school': 'chore/backpack',
  'eng.about-me': 'habit/smile',
  'eng.family-have-got': 'reward/picnic',
  'eng.classroom-2': 'chore/homework',
  'eng.food-meals': 'reward/choose-dinner',
  'eng.toys-clothes': 'reward/toy',
  'eng.body-feelings': 'reward/heart',
  'eng.actions-can': 'habit/exercise',
  'eng.animals-2': 'reward/zoo',
  'eng.seasons-weather': 'concept/sun',
  'eng.holidays': 'reward/gift',
  'eng.phonics-patterns': 'topic/phonics',
  'ukr.sounds-letters': 'topic/phonics',
  'ukr.syllables': 'reward/blocks',
  'ukr.consonants': 'topic/phonics',
  'ukr.sentence': 'habit/journal',
  'ukr.word-groups': 'reward/puzzle',
  'ukr.reading': 'reward/book',
  'ukr.vocabulary': 'concept/brain',
  'ukr.sounds-spelling': 'topic/phonics',
  'ukr.word-meaning': 'concept/brain',
  'ukr.word-structure': 'reward/blocks',
  'ukr.nouns-capitals': 'habit/pencil',
  'ukr.word-classes': 'reward/puzzle',
  'ukr.sentence-2': 'habit/journal',
  'ukr.text': 'chore/mail',
  'ukr.folklore': 'reward/music',
  'ukr.reading-comprehension-2': 'reward/book',
  'ukr.literature-genres': 'reward/book',
  'ukr.books-media': 'reward/movie',
  'svit.seasons': 'concept/sun',
  'svit.plants-animals': 'reward/garden',
  'svit.senses-body': 'concept/hand',
  'svit.hygiene-health': 'chore/soap',
  'svit.safety': 'concept/no-entry',
  'svit.family-community': 'reward/friends',
  'svit.professions': 'concept/grad-cap',
  'svit.ukraine': 'concept/star-badge',
  'svit.inquiry': 'reward/science',
  'svit.earth-space': 'concept/sunrise',
  'svit.space-planets': 'concept/moon',
  'svit.weather-observe': 'concept/sun',
  'svit.living-world-2': 'reward/tree',
  'svit.materials-water-air': 'chore/water',
  'svit.body-health-2': 'chore/medicine',
  'svit.emotions-friendship': 'habit/smile',
  'svit.family-home-2': 'reward/picnic',
  'svit.school-community-2': 'chore/backpack',
  'svit.ukraine-2': 'concept/star-badge',
  'svit.economy-2': 'concept/money-bag',
  'svit.safety-2': 'concept/no-entry',
  'svit.computer-information': 'reward/tablet',
  'svit.algorithms': 'reward/robot',
  'svit.inventions-energy': 'concept/flame',
};

/** Topic art by catalog topic id; `undefined` → emoji fallback. */
export function topicArt(topicId?: string | null): string | undefined {
  return topicId ? ICONS[TOPIC_ART[topicId] ?? ''] : undefined;
}

/**
 * Mastery-crown tier art (ADR-0034 §2) by crown level (`crown/<level>`) — four recolours of ONE crown
 * shape so the meter reads as the same crown filling: muted → lavender → silver → gold. `undefined` →
 * caller's emoji fallback.
 */
export function crownArt(crown?: string | null): string | undefined {
  return crown ? ICONS[`crown/${crown}`] : undefined;
}

/**
 * Reward-category icon is stored as a curated EMOJI (ADR-0036), not a key — so map the emoji → a shared pack
 * icon (almost all reuse the reward set, since the palette was built off the reward taxonomy). `undefined`
 * → render the emoji itself.
 */
const CATEGORY_ART: Record<string, string> = {
  '🎁': 'reward/gift',
  '📱': 'reward/screen',
  '🍬': 'reward/candy',
  '🍦': 'reward/icecream',
  '🍪': 'reward/treat',
  '🌳': 'reward/tree',
  '🎡': 'reward/outing',
  '🧸': 'reward/toy',
  '🎮': 'reward/game',
  '📚': 'reward/book',
  '⭐': 'reward/sticker',
  '💵': 'reward/money',
  '🎬': 'reward/movie',
  '🎨': 'reward/art',
  '⚽': 'reward/soccer',
  '🚲': 'reward/bike',
  '🏆': 'reward/trophy',
  '🐾': 'reward/pet',
  '🎵': 'reward/music',
  '🛏️': 'chore/bed',
  // Expansion (2026-06-24)
  '🍕': 'reward/pizza',
  '🍿': 'reward/popcorn',
  '🧁': 'reward/cupcake',
  '🍩': 'reward/donut',
  '🍎': 'reward/fruit',
  '🥤': 'reward/drink',
  '🏖️': 'reward/beach',
  '🏕️': 'reward/camping',
  '🦁': 'reward/zoo',
  '🏊': 'reward/swimming',
  '📺': 'reward/tv',
  '🎧': 'reward/headphones',
  '🧩': 'reward/puzzle',
  '🤖': 'reward/robot',
  '🎲': 'reward/dice',
  '🖍️': 'reward/crayons',
  '🔬': 'reward/science',
  '🏀': 'reward/basketball',
  '🛹': 'reward/skateboard',
  '🌙': 'reward/stay-up',
  '👫': 'reward/friends',
  '🍽️': 'reward/choose-dinner',
  '🐷': 'reward/piggy-bank',
  '👑': 'reward/crown',
  '🎈': 'reward/balloon',
  '❤️': 'reward/heart',
};

/** Reward-category art for a stored category emoji (ADR-0036); `undefined` → render the emoji. */
export function categoryArt(emoji?: string | null): string | undefined {
  if (!emoji) return undefined;
  const name = CATEGORY_ART[emoji];
  return name ? ICONS[name] : undefined;
}
